<script setup lang="ts">
/**
 * The link an organizer drops into a group text (SPEC §6.28).
 *
 * Three ways out of here, because there are three kinds of phone in any family:
 * copy it, hand it to whatever the browser can share with, or open a text
 * message with it already typed. Nothing here assumes the second two exist.
 *
 * The organizer is told what the link shows before they share it. A link that
 * turns out to name every attending household after it has gone into a
 * neighbourhood thread cannot be taken back.
 */
import { computed, ref, watch } from 'vue'
import { ApiError, endpoints, type EventShareLinkResponse } from '@/api'

const props = defineProps<{
  groupId: string
  eventId: string
  /** The meal's name, used in the message a text composer opens with. */
  title: string
  /** When it is, in words, for the same message. */
  when: string
  /** Loads only while this is true, so a closed dialog costs nothing. */
  open: boolean
}>()

const share = ref<EventShareLinkResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const copied = ref(false)
const replacing = ref(false)
const confirmingReplace = ref(false)

/** What just happened, for anybody not watching the button. */
const announcement = ref('')

/**
 * What this link shows, said in the organizer's words rather than the enum's.
 */
const shows = computed(() => {
  switch (share.value?.visibility) {
    case 'MenuOnly':
      return 'Anybody with this link sees the meal and the menu. No numbers, no names.'
    case 'MenuAndHouseholds':
      return 'Anybody with this link sees the meal, the menu, how many are coming, and which families are coming and bringing what.'
    default:
      return 'Anybody with this link sees the meal, the menu, and how many people are coming. No names.'
  }
})

/**
 * The message a text composer opens with.
 *
 * Short on purpose: it is a starting point somebody edits, and the link is the
 * part that has to survive being pasted.
 */
const message = computed(() =>
  share.value ? `${props.title} — ${props.when}. ${share.value.url}` : '',
)

/** Only where the browser has it. Feature-detected, never assumed. */
const canShare = computed(
  () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
)

/**
 * The SMS composer link.
 *
 * `sms:?&body=` rather than one of the two platform-specific spellings. iOS
 * wants `&body=` and Android wants `?body=`, and this form is the one both
 * have accepted for years.
 */
const smsHref = computed(() => `sms:?&body=${encodeURIComponent(message.value)}`)

function describe(caught: unknown): string {
  return (
    (caught instanceof ApiError && caught.problem?.title) ||
    'We could not get the link just then. Try again in a moment.'
  )
}

async function load() {
  loading.value = true
  error.value = null
  copied.value = false

  try {
    share.value = await endpoints.eventShareLink(props.groupId, props.eventId)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

async function copy() {
  if (!share.value) return

  try {
    await navigator.clipboard.writeText(share.value.url)
    copied.value = true
    announcement.value = 'Link copied.'
  } catch {
    // A browser that refuses clipboard access is not an error worth showing:
    // the link is on screen and selectable.
    copied.value = false
  }
}

async function hand() {
  if (!share.value) return

  try {
    await navigator.share({ title: props.title, text: message.value, url: share.value.url })
  } catch {
    // Includes the visitor closing the sheet, which is not a failure and must
    // not be reported as one.
  }
}

/**
 * Replaces the link.
 *
 * Behind a confirmation, because it is the one thing on this card that breaks
 * something: every copy already sitting in a thread stops working, and there
 * is no undo.
 */
async function replace() {
  confirmingReplace.value = false
  replacing.value = true
  error.value = null
  copied.value = false

  try {
    share.value = await endpoints.revokeEventShareLink(props.groupId, props.eventId)
    announcement.value = 'The old link has stopped working. This is the new one.'
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    replacing.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen && !share.value) void load()
  },
  { immediate: true },
)
</script>

<template>
  <section class="share">
    <p aria-live="polite" class="share__announcement">{{ announcement }}</p>

    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>

    <v-skeleton-loader v-if="loading" type="text" />

    <template v-else-if="share">
      <!--
        Selectable, and looking like something to be copied rather than read,
        so a browser that refuses clipboard access still leaves a way through.
      -->
      <p class="share__link" data-testid="share-url">{{ share.url }}</p>

      <p class="share__shows" data-testid="share-shows">{{ shows }}</p>

      <div class="share__actions">
        <v-btn class="share__action" color="primary" variant="flat" @click="copy">
          {{ copied ? 'Copied' : 'Copy link' }}
        </v-btn>

        <v-btn v-if="canShare" class="share__action" variant="outlined" @click="hand">
          Share
        </v-btn>

        <!--
          An anchor rather than a button. sms: is handled by the operating
          system, and a click handler that set location would be a click
          handler doing what a link already does.
        -->
        <v-btn class="share__action" variant="outlined" :href="smsHref" data-testid="share-sms">
          Open Messages
        </v-btn>
      </div>

      <v-btn
        class="share__replace"
        variant="text"
        size="small"
        :loading="replacing"
        @click="confirmingReplace = true"
      >
        Replace this link
      </v-btn>

      <v-dialog v-model="confirmingReplace" max-width="28rem">
        <v-card>
          <v-card-title tag="h2">Replace this link?</v-card-title>
          <v-card-text>
            Every copy of the old link stops working, including the ones already sitting in your
            family's messages. Anybody who taps one will be told to ask you for the new one.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="confirmingReplace = false">Keep it</v-btn>
            <v-btn color="error" variant="flat" @click="replace">Replace it</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </section>
</template>

<style scoped>
.share {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.share__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.share__link {
  margin: 0;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.875rem;
  overflow-wrap: anywhere;
  user-select: all;
}

.share__shows {
  margin: 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.share__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Thumb-sized, like every other target in this product (SPEC §9.5). */
.share__action {
  min-height: 2.75rem;
  flex: 1 1 8rem;
}

.share__replace {
  align-self: flex-start;
  min-height: 2.75rem;
}
</style>
